import { format } from 'date-fns';
import { local_signage } from '../../Const';

export async function fetchAndMapOrders(startDate, endDate) {
    const fromStr = format(startDate, 'yyyyMMdd');
    const toStr = format(endDate, 'yyyyMMdd');

    const url = `${local_signage}/api/v1/getsalesordermongodeliverydatelist?fromDate=${fromStr}&toDate=${toStr}`;

    const res = await fetch(url, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('AuthorizationToken') || ''}`,
        }
    });

    if (!res.ok) {
        throw new Error('Failed to fetch orders');
    }

    const json = await res.json();
    const rawOrders = Array.isArray(json.data) ? json.data : [];

    // Exclude HORECA explicitly.
    // For SBSCR (Subscription), only exclude if it's purely buying subscription points (no retail items).
    const retailOrders = rawOrders.filter(o => {
        const type = (o.TranSubTypeId || o.tranSubTypeId || '').toUpperCase();
        const saleOrderId = (o.SaleOrderId || o.saleOrderId || '').toUpperCase();
        
        if (type === 'HORECA') return false;
        if (saleOrderId.endsWith('-PWLT') || saleOrderId.endsWith('-PPPR')) return false;
        
        if (type === 'SBSCR') {
            const skus = Array.isArray(o.OrderSkuDetails || o.orderSkuDetails) ? (o.OrderSkuDetails || o.orderSkuDetails) : [];
            const hasRetailItems = skus.some(sku => {
                const div = (sku.Division || sku.division || '').toUpperCase();
                const fid = (sku.ItemFamilyID || sku.itemFamilyID || sku.itemFamilyId || '').toUpperCase();
                const cat = (sku.CategoryName || sku.categoryName || '').toUpperCase();
                return !(fid === 'SR' && div === 'OTHER') && cat !== 'SERVICE CATEGORY';
            });
            return hasRetailItems; // Keep if it actually has retail items
        }
        
        return true;
    });

    return retailOrders.map(mapApiOrderToDashboardFormat);
}

// ─────────────────────────────────────────────────────────────────
// Service Orders: Wallet Recharges (SBSCR) + Subscriptions (PREPD)
// These are intentionally excluded from retail dashboard but tracked separately here.
// ─────────────────────────────────────────────────────────────────
const SERVICE_TRAN_TYPES = new Set(['SBSCR', 'PREPD']);

export async function fetchAndMapServiceOrders(startDate, endDate) {
    const fromStr = format(startDate, 'yyyyMMdd');
    const toStr = format(endDate, 'yyyyMMdd');

    const url = `${local_signage}/api/v1/getsalesordermongodeliverydatelist?fromDate=${fromStr}&toDate=${toStr}`;

    const res = await fetch(url, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('AuthorizationToken') || ''}`,
        }
    });

    if (!res.ok) throw new Error('Failed to fetch service orders');

    const json = await res.json();
    const rawOrders = Array.isArray(json.data) ? json.data : [];

    // Keep ONLY purely wallet recharge and subscription orders (exclude SBSCR orders that are actually retail purchases)
    return rawOrders
        .filter(o => {
            const type = (o.TranSubTypeId || o.tranSubTypeId || '').toUpperCase();
            if (!SERVICE_TRAN_TYPES.has(type)) return false;
            
            if (type === 'SBSCR') {
                const skus = Array.isArray(o.OrderSkuDetails || o.orderSkuDetails) ? (o.OrderSkuDetails || o.orderSkuDetails) : [];
                const hasRetailItems = skus.some(sku => {
                    const div = (sku.Division || sku.division || '').toUpperCase();
                    const fid = (sku.ItemFamilyID || sku.itemFamilyID || sku.itemFamilyId || '').toUpperCase();
                    const cat = (sku.CategoryName || sku.categoryName || '').toUpperCase();
                    return !(fid === 'SR' && div === 'OTHER') && cat !== 'SERVICE CATEGORY';
                });
                return !hasRetailItems; // Exclude from service orders if it contains retail items
            }
            return true;
        })
        .map(o => {
            const tranType = (o.TranSubTypeId || o.tranSubTypeId || '').toUpperCase();
            const skus = Array.isArray(o.OrderSkuDetails || o.orderSkuDetails) ? (o.OrderSkuDetails || o.orderSkuDetails) : [];
            const skuAmount = skus.reduce((s, sku) => s + (sku.TotalAmount || sku.totalAmount || 0), 0);
            const amount = skuAmount || o.TotalAmount || o.totalAmount || 0;
            // Use DeliveryScheduleDate first — API fetches by this field, so date must align
            const rawDate = o.DeliveryScheduleDate || o.deliveryScheduleDate || o.BillDate || o.billDate || o.CustomerOrderDate || o.customerOrderDate;
            const date = rawDate ? new Date(rawDate).toISOString().split('T')[0] : null;
            const rawAreaName = o.AreaName || o.areaName || o.AreaId || o.areaId || 'Unknown';
            const rawStatus = (o.OrderStatusId || o.orderStatusId || '').toUpperCase();
            const STATUS_MAP = { DL: 'Delivered', PN: 'Pending', PL: 'Pending', CN: 'Cancelled', OD: 'Out for Delivery' };
            const orderStatus = STATUS_MAP[rawStatus] || rawStatus || 'Unknown';
            return {
                id: o.SaleOrderId || o.saleOrderId || o._id,
                saleOrderId: o.SaleOrderId || o.saleOrderId || o._id,
                customerId: o.CustomerId || o.customerId || 'Unknown',
                customerName: (o.Email || o.email) ? (o.Email || o.email).split('@')[0] : 'Unknown',
                date,
                type: tranType === 'SBSCR' ? 'Wallet Recharge' : 'Subscription',
                tranSubTypeId: tranType,
                amount,
                orderStatus,
                cityName: normalizeCityName(rawAreaName),
                cityId: normalizeCityName(rawAreaName),
                storeName: o.StoreName || o.storeName || o.DeliveryBranchId || o.deliveryBranchId || 'Unknown',
                storeId: o.StoreName || o.storeName || o.DeliveryBranchId || o.deliveryBranchId || 'Unknown',
                billNo: o.BillNo || o.billNo || null,
                grade: o.Grade || o.grade || null,
                _original: o,
            };
        });
}


const CITY_NAME_MAP = {
    'Nagpur City Area': 'Nagpur City',
};

function normalizeCityName(name) {
    if (!name) return name;
    return CITY_NAME_MAP[name] || name;
}

function mapApiOrderToDashboardFormat(apiOrder) {
    const id = apiOrder.SaleOrderId || apiOrder.saleOrderId || apiOrder._id;
    const customerId = apiOrder.CustomerId || apiOrder.customerId || 'Unknown';
    const emailVal = apiOrder.Email || apiOrder.email;
    const customerName = emailVal ? emailVal.split('@')[0] : 'Unknown Customer';

    const storeId = apiOrder.StoreName || apiOrder.storeName || apiOrder.DeliveryBranchId || apiOrder.deliveryBranchId || 'Unknown Store';
    const storeName = apiOrder.StoreName || apiOrder.storeName || `Store ${storeId}`;
    const pincode = apiOrder.Pincode || apiOrder.pincode || 'Unknown';

    const rawAreaName = apiOrder.AreaName || apiOrder.areaName || apiOrder.AreaId || apiOrder.areaId || 'CITY-UNKNOWN';
    const cityId = normalizeCityName(rawAreaName);
    const cityName = normalizeCityName(apiOrder.AreaName || apiOrder.areaName || 'Unknown City');
    const grade = apiOrder.Grade || apiOrder.grade || 'NONE';
    
    const ordersSoFar = apiOrder.OrdersSoFar !== undefined ? apiOrder.OrdersSoFar : (apiOrder.ordersSoFar !== undefined ? apiOrder.ordersSoFar : 0);
    const isNewCustomer = ordersSoFar === 0;

    // Dates - Prioritize DeliveryScheduleDate as backend fetches based on Delivery Date
    const deliveryScheduleDate = apiOrder.DeliveryScheduleDate || apiOrder.deliveryScheduleDate;
    const customerOrderDate = apiOrder.CustomerOrderDate || apiOrder.customerOrderDate;
    const orderDateRaw = apiOrder.OrderDate || apiOrder.orderDate;
    const deliveryDate = apiOrder.DeliveryDate || apiOrder.deliveryDate;
    const billDate = apiOrder.BillDate || apiOrder.billDate;
    const closedVal = apiOrder.Closed || apiOrder.closed;

    const orderDateObj = new Date(deliveryScheduleDate || customerOrderDate || orderDateRaw || new Date());
    const year = orderDateObj.getFullYear();
    const month = String(orderDateObj.getMonth() + 1).padStart(2, '0');
    const day = String(orderDateObj.getDate()).padStart(2, '0');
    const date = `${year}-${month}-${day}`;
    const time = `${String(orderDateObj.getHours()).padStart(2, '0')}:${String(orderDateObj.getMinutes()).padStart(2, '0')}`;

    // Status
    let status = 'Processing';
    const orderStatusId = (apiOrder.OrderStatusId || apiOrder.orderStatusId || '').toUpperCase();
    if (orderStatusId === 'CN' || orderStatusId === 'CANCELLED') status = 'Cancelled';
    else if (deliveryDate) status = 'Delivered';
    else if (closedVal) status = 'Delivered';
    else {
        // If scheduled for the past and not cancelled, assume delivered for now so charts look populated
        const scheduledDate = deliveryScheduleDate ? new Date(deliveryScheduleDate) : null;
        if (scheduledDate && scheduledDate < new Date()) {
            status = 'Delivered';
        }
    }

    // Payment Method & Gateway (API returns camelCase or PascalCase fields)
    let paymentMethod = 'Online';
    let paymentGateway = 'Unknown';
    const billDetails = apiOrder.OrderBillDetails || apiOrder.orderBillDetails || [];
    if (billDetails && billDetails.length > 0) {
        const bill = billDetails[0];
        const pm = bill.paymentGatewayID || bill.PaymentGatewayID || bill.paymentMethodId || bill.PaymentMethodId || bill.paymentGatewayId || bill.paymentMethodID;
        paymentGateway = bill.paymentGatewayName || bill.PaymentGatewayName || 'Unknown';
        if (pm === 'COD' || (pm && typeof pm === 'string' && pm.toUpperCase().includes('CASH')) || (paymentGateway && typeof paymentGateway === 'string' && paymentGateway.toUpperCase().includes('CASH'))) {
            paymentMethod = 'COD';
        }
    }

    // Delivery Type (Same Day vs Next Day)
    let deliveryType = 'Same Day';
    if (customerOrderDate && deliveryScheduleDate) {
        const orderDateStr = customerOrderDate.split('T')[0];
        const scheduleDateStr = deliveryScheduleDate.split('T')[0];
        if (orderDateStr !== scheduleDateStr) {
            deliveryType = 'Next Day';
        }
    }

    // Delay Status
    let isDelayed = false;
    if (status === 'Delivered' && deliveryDate && deliveryScheduleDate) {
        isDelayed = new Date(deliveryDate) > new Date(deliveryScheduleDate);
    } else if (status === 'Processing' && deliveryScheduleDate) {
        isDelayed = new Date() > new Date(deliveryScheduleDate);
    }

    // Cancellation Reason (Mocked for Demo since API lacks it)
    let cancelReason = null;
    if (status === 'Cancelled') {
        const hash = id.charCodeAt(id.length - 1) || 0;
        cancelReason = hash % 2 === 0 ? 'Due to delay' : 'Due to OOS';
    }

    // Items — exclude service/subscription line items (Division=OTHER, ItemFamilyID=SR)
    const EXCLUDED_ITEM_FAMILIES = new Set(['SR']);
    const EXCLUDED_ITEM_DIVISIONS = new Set(['OTHER']);

    const skuDetailsList = apiOrder.OrderSkuDetails || apiOrder.orderSkuDetails || [];
    const items = skuDetailsList
        .filter(sku => {
            const div = (sku.Division || sku.division || '').toUpperCase();
            const fid = (sku.ItemFamilyID || sku.itemFamilyID || sku.itemFamilyId || '').toUpperCase();
            const cat = (sku.CategoryName || sku.categoryName || '').toUpperCase();
            // Skip Service Category items
            if (EXCLUDED_ITEM_FAMILIES.has(fid) && EXCLUDED_ITEM_DIVISIONS.has(div)) return false;
            if (cat === 'SERVICE CATEGORY') return false;
            return true;
        })
        .map(sku => {
            const resolvedSkuId = sku.SkuId || sku.skuId || sku.ItemId || sku.itemId || 'UNKNOWN';
            const resolvedSkuName = sku.SkuName || sku.skuName || sku.ItemName || sku.itemName || 'Unknown Product';
            const resolvedDivision = sku.Division || sku.division || 'CAT-OTH';
            const resolvedCategoryName = sku.CategoryName || sku.categoryName || sku.Division || sku.division || 'Other';
            const resolvedItemFamilyId = sku.ItemFamilyID || sku.itemFamilyID || sku.itemFamilyId || '';
            const resolvedRate = sku.Rate || sku.rate || sku.ItemRate || sku.itemRate || 0;
            const resolvedQuantity = sku.Quantity || sku.quantity || 1;
            const resolvedUnitQuantity = sku.ItemUnitQuantity || sku.itemUnitQuantity || 0;
            const resolvedTotalAmount = sku.TotalAmount || sku.totalAmount || sku.ProductAmount || sku.productAmount || 0;

            return {
                productId: resolvedSkuId,
                productName: resolvedSkuName,
                categoryId: resolvedDivision,
                categoryName: resolvedCategoryName,
                itemFamilyId: resolvedItemFamilyId,
                price: resolvedRate,
                quantity: resolvedQuantity,
                unitQuantity: resolvedUnitQuantity,
                total: resolvedTotalAmount,
            };
        });

    // Categorization: uses Division + ItemFamilyID + product name (in priority order)
    items.forEach(item => {
        const name = item.productName.toLowerCase();
        const div = (item.categoryId || '').toUpperCase(); // Division field
        const fid = (item.itemFamilyId || '').toUpperCase(); // ItemFamilyID field

        if (div === 'EGG') {
            // All EGG division items → Eggs
            item.categoryId = 'CAT-EGG';
            item.categoryName = 'Eggs';

        } else if (div === 'POULTRY') {
            // POULTRY division: distinguish Chicken vs RTC Non-Veg
            const isRtc = fid === 'RTC' ||
                name.includes('rtc') || name.includes('fried') ||
                name.includes('bite') || name.includes('fingers') ||
                name.includes('popcorn') || name.includes('nugget') ||
                name.includes('sausage') || name.includes('salami') ||
                name.includes('kabab') || name.includes('kebab');

            if (isRtc) {
                item.categoryId = 'CAT-RTC-NV';
                item.categoryName = 'RTC Non-Veg';
            } else {
                // SD (Special Dress), DR (Dress), and all other POULTRY → Chicken
                item.categoryId = 'CAT-RAW';
                item.categoryName = 'Chicken';
            }

        } else if (div === 'PRODUCT') {
            // PRODUCT division: check for mutton/goat by name
            const isMutton = name.includes('mutton') || name.includes('goat') ||
                name.includes('lamb');
            const isFish = name.includes('fish') || name.includes('prawn') ||
                name.includes('shrimp') || name.includes('salmon') ||
                name.includes('tuna') || name.includes('pomfret');
            const isRtcVeg = name.includes('veg') && (
                name.includes('rtc') || name.includes('burger') ||
                name.includes('nugget') || name.includes('patty')
            );

            if (isMutton) {
                item.categoryId = 'CAT-MUTTON';
                item.categoryName = 'Mutton';
            } else if (isFish) {
                item.categoryId = 'CAT-RTC-NV';
                item.categoryName = 'RTC Non-Veg';
            } else if (isRtcVeg) {
                item.categoryId = 'CAT-RTC-V';
                item.categoryName = 'RTC Veg';
            } else if (name.includes('rtc') || name.includes('fried') ||
                name.includes('nugget') || name.includes('sausage') ||
                name.includes('salami') || name.includes('kabab')) {
                item.categoryId = 'CAT-RTC-NV';
                item.categoryName = 'RTC Non-Veg';
            } else {
                item.categoryId = 'CAT-OTH';
                item.categoryName = 'Others';
            }

        } else if (div === 'OIL' || name.includes('oil')) {
            item.categoryId = 'CAT-OIL';
            item.categoryName = 'Oil';

        } else if (name.includes('masala') || name.includes('spice')) {
            item.categoryId = 'CAT-MSL';
            item.categoryName = 'Masala';

        } else {
            // null / unknown Division — use CategoryName + ItemFamilyID + product name as fallback
            const catNameUp = (item.categoryName || '').toUpperCase();
            const isWholeBird = catNameUp.includes('WHOLE BIRD') || catNameUp.includes('WHOLE');
            const isChickenName = name.includes('chicken') || name.includes('breast') ||
                name.includes('liver') || name.includes('gizzard') ||
                name.includes('wing') || name.includes('leg') ||
                name.includes('thigh') || name.includes('boneless') ||
                name.includes('bird') || fid === 'RT' || fid === 'SD' || fid === 'DR';

            if (isWholeBird || isChickenName) {
                item.categoryId = 'CAT-RAW';
                item.categoryName = 'Chicken';
            } else if (name.includes('mutton') || name.includes('goat') || name.includes('lamb')) {
                item.categoryId = 'CAT-MUTTON';
                item.categoryName = 'Mutton';
            } else if (name.includes('egg')) {
                item.categoryId = 'CAT-EGG';
                item.categoryName = 'Eggs';
            } else {
                item.categoryId = 'CAT-OTH';
                item.categoryName = 'Others';
            }
        }
    });

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    // Use the actual ItemUnitQuantity * Quantity if available, otherwise fallback to rough estimate
    const volumeKg = items.reduce((sum, item) => sum + (item.unitQuantity > 0 ? (item.unitQuantity * item.quantity) : (item.quantity * 0.5)), 0);
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);

    const discountVal = apiOrder.DiscountAmount || apiOrder.discountAmount || 0;
    const discountCode = apiOrder.DiscountCode || apiOrder.discountCode || null;
    const deliveryFee = apiOrder.DeliveryCharge || apiOrder.deliveryCharge || 0;
    const apiTotalVal = apiOrder.TotalAmount || apiOrder.totalAmount;

    return {
        id,
        customerId,
        customerName,
        storeId,
        storeName,
        cityId,
        cityName,
        pincode,
        date,
        time,
        items,
        itemCount,
        volumeKg,
        subtotal,
        discount: discountVal,
        couponCode: discountCode,
        deliveryFee: deliveryFee,
        total: apiTotalVal || (subtotal - discountVal + deliveryFee),
        paymentMethod,
        paymentGateway,
        status,
        deliveryType,
        isDelayed,
        cancelReason,
        isNewCustomer,
        grade,

        // New Delivery fields mapped
        deliveryScheduleDate: deliveryScheduleDate || null,
        pickUpDate: apiOrder.PickUpDate || apiOrder.pickUpDate || null,
        deliveryDate: deliveryDate || null,
        timeSlotId: apiOrder.TimeSlotId || apiOrder.timeSlotId || null,
        timeSlotName: apiOrder.TimeSlotName || apiOrder.timeSlotName || null,
        orderTakenByBranchId: apiOrder.OrderTakenByBranchId || apiOrder.orderTakenByBranchId || null,
        orderTakenByEmployeeId: apiOrder.OrderTakenByEmployeeId || apiOrder.orderTakenByEmployeeId || null,
        orderBillDate: billDate || null,
        orderBillNo: apiOrder.BillNo || apiOrder.billNo || null,
        customerOrderDate: customerOrderDate || null,
        orderDateRaw: orderDateRaw || null,

        _original: apiOrder
    };
}
